import React, { useState, useEffect } from "react";
import {
  X,
  Download,
  Users,
  Settings,
  Mail,
  CheckCircle,
  Eye,
  EyeOff,
  Zap,
  Hash,
  Edit3,
  Save,
  Phone,
} from "lucide-react";

const SmsExcelModal = ({
  isOpen,
  onClose,
  onSubmit,
  templateParams,
  groupUsers,
  setTemplateParams,
}) => {
  const [excelData, setExcelData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [showAllRows, setShowAllRows] = useState(false);
  const [quickFillValues, setQuickFillValues] = useState({});

  useEffect(() => {
    if (isOpen && groupUsers.length > 0 && excelData.length === 0) {
      const initialData = groupUsers.map((user) => {
        const paramData = {};
        templateParams.forEach((param) => {
          paramData[param.key] = param.value || "";
        });
        return {
          phone: user.phone,
          ...paramData,
        };
      });
      setExcelData(initialData);
    }
  }, [isOpen, groupUsers]);

  const handleCellEdit = (rowIndex, key, value) => {
    setExcelData((prev) =>
      prev.map((row, index) =>
        index === rowIndex ? { ...row, [key]: value } : row
      )
    );
  };

  const handleCellClick = (rowIndex, key) => {
    setEditingCell({ row: rowIndex, key });
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleDownloadExcel = () => {
    const headers = ["phone", ...templateParams.map((p) => p.key)];
    const data = excelData.map((row) => {
      const rowData = { phone: row.phone };
      templateParams.forEach((param) => {
        rowData[param.key] = row[param.key] || "";
      });
      return rowData;
    });

    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((header) => `"${row[header] || ""}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "message_data.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = () => {
    const data = excelData.map((row) => {
      const rowData = { phone: row.phone };
      templateParams.forEach((param) => {
        rowData[param.key] = row[param.key] || "";
      });
      return rowData;
    });
    onSubmit(data);
    onClose();
  };

  const fillAllWithSameValue = (key, value) => {
    setExcelData((prev) =>
      prev.map((row) => ({
        ...row,
        [key]: value,
      }))
    );
  };

  const handleQuickFill = (key, value) => {
    if (!value) return;

    // 1. Update all excelData rows
    fillAllWithSameValue(key, value);

    // 2. Update templateParams to store the value
    setTemplateParams((prev) => {
      const updated = prev.map((param) =>
        param.key === key ? { ...param, value } : param
      );
      // If key not found, add it
      if (!updated.find((p) => p.key === key)) {
        updated.push({ key, value });
      }
      return updated;
    });

    // 3. Clear input
    setQuickFillValues((prev) => ({
      ...prev,
      [key]: "",
    }));
  };

  console.log(templateParams, "templateParams");

  if (!isOpen) return null;

  const displayedRows = showAllRows ? excelData : excelData.slice(0, 100);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 px-8 py-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                <Settings className="w-7 h-7" />
              </div>
              <div>
                <div className="text-2xl text-white font-bold leading-tight">
                  Configure Message Parameters
                </div>
                <span className="text-blue-100 text-sm mt-1 font-medium">
                  Enter individual parameters for each recipient
                </span>
              </div>
            </div>
            <div
              onClick={onClose}
              className="p-3 hover:bg-white/20 rounded-2xl transition-all duration-200 hover:scale-105"
            >
              <X className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 p-8 overflow-y-auto bg-gray-50/30">
          {/* Quick Fill */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-800">
                  Quick Fill Options
                </div>
                <span className="text-gray-600 text-sm">
                  Fill all cells in a column with the same value
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templateParams.map((param) => (
                <div key={param.key} className="group">
                  <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-3">
                    <Hash className="w-4 h-4 text-gray-500" />
                    <span>{param.key}</span>
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={quickFillValues[param.key] || ""}
                      placeholder={`Fill all ${param.key} values`}
                      className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-purple-500 focus:outline-none transition-all duration-200 bg-white shadow-sm hover:shadow-md text-gray-800 placeholder-gray-400"
                      onChange={(e) =>
                        setQuickFillValues((prev) => ({
                          ...prev,
                          [param.key]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleQuickFill(
                            param.key,
                            quickFillValues[param.key]
                          );
                        }
                      }}
                    />

                    <div
                      onClick={() =>
                        handleQuickFill(param.key, quickFillValues[param.key])
                      }
                      className="px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      <Save className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundImage:
                      "linear-gradient(to bottom right, #22c55e, #16a34a)",
                    borderRadius: "1rem",
                    boxShadow:
                      "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
                  }}
                >
                  <Users
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      color: "white",
                    }}
                  />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-800">
                    Recipients Data
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                      {groupUsers.length} users
                    </span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-semibold rounded-full">
                      Click cells to edit
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div
                  onClick={() => setShowAllRows(!showAllRows)}
                  className="flex items-center space-x-2 px-5 py-3 text-gray-600 hover:text-gray-800 hover:bg-white/80 rounded-2xl transition-all duration-200 border border-gray-200 hover:border-gray-300 hover:shadow-md"
                >
                  {showAllRows ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">
                    {showAllRows ? "Show Less" : "Show All"}
                  </span>
                </div>
                <div
                  onClick={handleDownloadExcel}
                  className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-3 rounded-2xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-medium"
                >
                  <Download className="w-5 h-5" />
                  <span>Download CSV</span>
                </div>
              </div>
            </div>

            <div className="border-2 border-gray-200 rounded-3xl overflow-hidden shadow-sm bg-white">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        <div className="flex items-center space-x-3">
                          <Phone className="w-5 h-5 text-gray-500" />
                          <span>Phone</span>
                        </div>
                      </th>
                      {templateParams.map((param) => (
                        <th
                          key={param.key}
                          className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                        >
                          <div className="flex items-center space-x-3">
                            <Edit3 className="w-5 h-5 text-gray-500" />
                            <span>{param.key}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {displayedRows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={`hover:bg-blue-50/50 transition-colors duration-150 ${
                          rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span>{row.phone}</span>
                          </div>
                        </td>
                        {templateParams.map((param) => (
                          <td
                            key={param.key}
                            className="px-6 py-4 whitespace-nowrap text-sm cursor-pointer rounded-lg"
                            onClick={() => handleCellClick(rowIndex, param.key)}
                          >
                            {editingCell?.row === rowIndex &&
                            editingCell?.key === param.key ? (
                              <input
                                type="text"
                                value={row[param.key] || ""}
                                onChange={(e) =>
                                  handleCellEdit(
                                    rowIndex,
                                    param.key,
                                    e.target.value
                                  )
                                }
                                onBlur={handleCellBlur}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    handleCellBlur();
                                  }
                                }}
                                className="w-[120px] px-3 py-2 border-2 border-blue-400 rounded-xl focus:outline-none focus:border-blue-600 bg-white text-gray-800"
                                autoFocus
                              />
                            ) : (
                              <div className="min-h-[2rem] flex items-center">
                                {row[param.key] ? (
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 rounded-xl font-medium border border-blue-200 hover:from-blue-100 hover:to-blue-200 transition-colors duration-150">
                                    {row[param.key]}
                                  </span>
                                ) : (
                                  <span className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-150">
                                    Click to edit
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!showAllRows && excelData.length > 100 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200">
                  <p className="text-center text-sm text-gray-600 font-medium">
                    Showing first 100 of {excelData.length} recipients
                    <span className="ml-2 text-xs text-gray-500">
                      • Click "Show All" to view complete list
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-8 py-6 border-t-2 border-gray-100 rounded-b-3xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">
                {groupUsers.length} recipients • {templateParams.length}{" "}
                parameters
              </span>
              <span className="ml-2 text-xs text-gray-500">
                • Click any cell to edit individual values
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div
                onClick={onClose}
                className="flex items-center space-x-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
              >
                <X className="w-5 h-5" />
                <span>Cancel</span>
              </div>
              <div
                onClick={handleSubmit}
                className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
              >
                <CheckCircle className="w-5 h-5" />
                <span>Apply Parameters</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsExcelModal;
